import React, { useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../../components/ui/card';
import { Skeleton } from '../../../components/ui/skeleton';
import { Badge } from '../../../components/ui/badge';
import { IconChartBar, IconAlertTriangle } from '@tabler/icons-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import coverageVerificationAnalyticService from '../../../services/coverage-verification-analytics.service';

const TherapeuticClassChart = ({ organization, dateRange, refreshKey }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const result = await coverageVerificationAnalyticService.getTherapeuticClassAnalysis({
          organization,
          dateRange,
          limit: 15,
        });
        setData(result.therapeutic_classes || []);
      } catch (err) {
        console.error('Failed to fetch therapeutic class data:', err);
        setError('Failed to load therapeutic class analysis');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [organization, dateRange, refreshKey]);

  if (loading) {
    return (
      <Card className="shadow-sm border-0">
        <CardHeader>
          <CardTitle className="text-base font-bold">Therapeutic Class Analysis</CardTitle>
          <CardDescription className="text-sm">
            Coverage trends by drug therapeutic class
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Skeleton className="w-full h-96" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="shadow-sm border-0">
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 text-red-600">
            <IconAlertTriangle size={20} />
            {error}
          </div>
        </CardContent>
      </Card>
    );
  }

  const chartData = data.map((item) => ({
    name: item.therapeutic_class.length > 30 
      ? item.therapeutic_class.substring(0, 30) + '...' 
      : item.therapeutic_class,
    fullName: item.therapeutic_class,
    'Check Count': item.check_count,
    'Coverage Rate': item.coverage_rate,
    'PA Rate': item.pa_rate,
  }));

  const getCoverageColor = (value) => {
    if (value >= 80) return 'default';
    if (value >= 50) return 'warning';
    return 'destructive';
  };

  return (
    <Card className="shadow-sm border-0">
      <CardHeader>
        <div className="flex items-center gap-2">
          <IconChartBar size={20} />
          <div className="flex-1">
            <CardTitle className="text-base font-bold">Therapeutic Class Analysis</CardTitle>
            <CardDescription className="text-sm">
              Coverage trends by drug therapeutic class
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {data.length === 0 ? (
          <div className="text-center text-muted-foreground py-8">No therapeutic class data available</div>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart 
                data={chartData}
                layout="vertical"
                margin={{ top: 5, right: 30, left: 120, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis type="number" className="text-xs" />
                <YAxis dataKey="name" type="category" width={120} className="text-xs" />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--background))', 
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '6px'
                  }} 
                />
                <Legend />
                <Bar dataKey="Check Count" fill="#3b82f6" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>

            {/* Detailed Table */}
            <div className="mt-6">
              <h6 className="text-sm font-semibold mb-3">Detailed Breakdown</h6>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Therapeutic Class</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Checks</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Unique Drugs</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Covered</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Coverage Rate</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">PA Rate</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Medical Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((item, index) => (
                      <tr key={index} className="border-b hover:bg-muted/50 transition-colors">
                        <td className="py-3 px-4">
                          <span className="font-medium">{item.therapeutic_class}</span>
                        </td>
                        <td className="py-3 px-4 text-right">{item.check_count.toLocaleString()}</td>
                        <td className="py-3 px-4 text-right text-muted-foreground">{item.unique_drugs}</td>
                        <td className="py-3 px-4 text-right">{item.covered_count.toLocaleString()}</td>
                        <td className="py-3 px-4 text-right">
                          <Badge variant={getCoverageColor(item.coverage_rate)}>
                            {item.coverage_rate.toFixed(1)}%
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <span className="text-sm text-orange-600 font-medium">
                            {item.pa_rate.toFixed(1)}%
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <span className="text-sm text-blue-600 font-medium">
                            {item.medical_coverage_rate.toFixed(1)}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default TherapeuticClassChart;
